/**
 * Basic human small-talk for Jet (local, instant — no API required).
 */

import { pickVariant } from './jetVoice.js';

const SMALLTALK_PATTERN =
  /^(how are you( doing)?|how('s| is) it going|how('re| are) things|what'?s up|sup|you good|you ok|you okay|how('s| is) your day|how have you been|thanks|thank you|thx|ty|appreciate it|you'?re (the best|awesome|great)|nice|cool|lol|haha|ok(ay)?|sounds good|got it|bye|goodbye|see you|later|good (morning|afternoon|evening|night)|who made you|are you (real|a bot|human|an ai)|what can you (do|help with))\b/i;

/**
 * @param {string} message
 */
export function isJetSmalltalk(message) {
  const q = String(message || '')
    .trim()
    .toLowerCase()
    .replace(/[?!.,]+$/g, '')
    .trim();
  if (!q || q.length > 80) return false;
  return SMALLTALK_PATTERN.test(q);
}

/**
 * @param {string} message
 * @param {{ seed?: number, role?: string }} [context]
 */
export function replyToJetSmalltalk(message, context = {}) {
  const q = String(message || '')
    .trim()
    .toLowerCase()
    .replace(/[?!.,]+$/g, '')
    .trim();
  const seed = context.seed ?? Date.now();

  if (/thanks|thank you|thx|ty|appreciate it/.test(q)) {
    return {
      text: pickVariant(
        [
          'Anytime.',
          'You got it.',
          'Happy to help.',
          'Sure thing — ping me if you need anything else.',
        ],
        seed
      ),
      links: [],
    };
  }

  if (/bye|goodbye|see you|later|good night/.test(q)) {
    return {
      text: pickVariant(
        [
          'Catch you later.',
          'Later — I’ll be here when you’re back.',
          'See you. Good luck with the demand.',
        ],
        seed
      ),
      links: [],
    };
  }

  if (/good (morning|afternoon|evening)/.test(q)) {
    return {
      text: pickVariant(
        [
          'Hey — hope your day’s going okay. What can I help with?',
          'Morning! Need a demand started, or just have a quick question?',
          'Hey there. I’m ready when you are.',
        ],
        seed
      ),
      links: [],
    };
  }

  if (/ok(ay)?$|sounds good|got it|cool|nice|lol|haha/.test(q)) {
    return {
      text: pickVariant(
        ['Cool.', 'Sounds good.', 'Alright.', 'Perfect — I’m here if you need the next step.'],
        seed
      ),
      links: [],
    };
  }

  if (/you'?re (the best|awesome|great)/.test(q)) {
    return {
      text: pickVariant(
        ['Appreciate that.', 'Thanks — that means a lot coming from someone chasing invoices.', 'Ha, thanks. Let’s get you paid.'],
        seed
      ),
      links: [],
    };
  }

  if (/are you (real|a bot|human|an ai)|who made you/.test(q)) {
    return {
      text: pickVariant(
        [
          'I’m Jet — DemandJet’s assistant. Not human, but I try to talk like a useful teammate.',
          'Built into DemandJet. I’m software, not a person — still happy to chat and help you move a demand.',
        ],
        seed
      ),
      links: [],
    };
  }

  if (/what can you (do|help with)/.test(q)) {
    return {
      text: pickVariant(
        [
          'I can help send a demand, read evidence into the form, check pricing, or talk through jurisdiction questions like collection costs.',
          'Pretty much: demands, debtors, evidence, pricing, and those “can I add fees in Ohio?” questions. What’s on your mind?',
        ],
        seed
      ),
      links: [
        { label: 'New demand', to: '/client/demands/new' },
        { label: 'Guides', to: '/client/guides' },
      ],
    };
  }

  // Default: how are you / what's up family
  return {
    text: pickVariant(
      [
        'Doing well — thanks for asking. How about you? Need help with a demand, or just saying hi?',
        'Pretty good! Ready when you are. Want to start a demand or ask something quick?',
        'I’m solid — can’t complain. What are you working on?',
        'Good, thanks. I’m here if you want to talk demands, evidence, or just vent about unpaid invoices.',
      ],
      seed
    ),
    links: [],
  };
}

/**
 * Follow-ups that continue a casual chat (short, social, not product FAQ).
 */
export function isJetConversationalFollowUp(message, { hadRecentSmalltalk = false } = {}) {
  const raw = String(message || '').trim();
  if (!raw || raw.length > 120) return false;
  if (isJetSmalltalk(raw)) return true;
  if (!hadRecentSmalltalk) return false;
  const q = raw.toLowerCase();
  // User answered Jet's "how about you?"
  if (/^(i'?m |im |doing |feeling |not bad|great|fine|tired|busy|ok|okay|alright|same|can'?t complain)/.test(q)) {
    return true;
  }
  if (/^(just |only )?(saying hi|checking in|chatting)/.test(q)) return true;
  return false;
}
